import { betrag, kontobezeichnung, kurswert, periodenname, t } from './sprache'
import type { Sprachschluessel } from './sprache'
import type { Rechenschritt, Rechenweg } from './typen'

/**
 * **Der Rechenweg einer Währungsdifferenz** (2.12 Abschnitt 3.21, R1652,
 * R1653) — als Mouse-over am Betrag und als Name in der Belegspalte.
 *
 * Der Befund vom 2026-10-12: Im Wertenachweis von 2100 Kapitalrücklage der
 * Tochter USA in Euro stand −1.700,68 ohne Herleitung, und die Zeile hieß
 * „Anpassung an den Stichtagskurs“, obwohl sie an den Eintrittskurs anpasst.
 * Der Auftrag: *„Der einfache, rein mathematische Rechenweg unter Nennung der
 * Variablen, jedoch ohne weiteren Erläuterungstext reicht.“*
 */

/** Der Name der Zeile nach dem Kurs, an den sie anpasst (R1653). */
const ZIELNAME: Record<string, Sprachschluessel> = {
  stichtag: 'nachweis.beleg.stichtagskurs',
  historisch: 'nachweis.beleg.historischerkurs',
  durchschnitt: 'nachweis.beleg.durchschnittskurs',
  gegenbuchung: 'nachweis.beleg.gegenbuchung',
}

const KURSNAME: Record<string, Sprachschluessel> = {
  stichtag: 'nachweis.rechenweg.stichtag',
  durchschnitt: 'nachweis.rechenweg.durchschnitt',
  historisch: 'nachweis.rechenweg.historisch',
  eintritt: 'nachweis.rechenweg.eintritt',
}

/** Woran die Zeile anpasst — nach dem Zusammenfassen auch mehreres. */
export function ziele(w: Rechenweg): string[] {
  return w.ziele ?? [w.ziel]
}

/** Die Namen der Zeile, je Ziel einer (R1653). */
export function belegnamen(w: Rechenweg): string[] {
  return ziele(w).flatMap((z) => (ZIELNAME[z] ? [t(ZIELNAME[z]!)] : []))
}

/** Der Kurs mit seinem Namen und Monat: „Durchschnittskurs Juli 2026“ — der Eintrittskurs ohne Monat. */
function kursname(s: Rechenschritt): string {
  const name = s.kursart && KURSNAME[s.kursart] ? t(KURSNAME[s.kursart]!) : ''
  if (s.kursart === 'eintritt' || s.periode === null) return name
  const zeitraum =
    s.von !== null && s.von !== s.periode
      ? `${periodenname(s.von)} – ${periodenname(s.periode)}`
      : periodenname(s.periode)
  return `${name} ${zeitraum}`
}

/**
 * Der Text des Mouse-overs: Zielwert minus umgerechnete Zeilen gleich
 * Differenz (R1652). Jede Zeile eine Rechnung, die Kurse mit allen
 * gespeicherten Stellen (R1083):
 *
 * ```
 *   40.000,00 × 0,8333333 Eintrittskurs = 33.333,33
 * − 35.034,01 umgerechnete Zeilen
 * = −1.700,68
 * ```
 */
export function rechenwegtext(w: Rechenweg): string {
  const zeilen = w.schritte.map((s, i) => {
    // Ab dem zweiten Schritt steht das Vorzeichen vorn, die Zahl ohne.
    const vor = i === 0 ? '' : s.ergebnis < 0 ? '− ' : '+ '
    const zahl = (x: number) => betrag(i === 0 ? x : Math.abs(x))
    if (s.konto !== null) return `${vor}${zahl(s.ergebnis)} ${kontobezeichnung(s.konto)}`
    return `${vor}${zahl(s.betrag ?? 0)} × ${kurswert(s.kurs ?? 0)} ${kursname(s)} = ${zahl(s.ergebnis)}`
  })

  if (w.zeilen === 0) {
    if (w.schritte.length > 1) zeilen.push(`= ${betrag(w.differenz)}`)
    return zeilen.join('\n')
  }

  if (w.schritte.length > 1) zeilen.push(`= ${betrag(w.zielwert)}`)
  zeilen.push(`${w.zeilen < 0 ? '+' : '−'} ${betrag(Math.abs(w.zeilen))} ${t('nachweis.rechenweg.zeilen')}`)
  zeilen.push(`= ${betrag(w.differenz)}`)
  return zeilen.join('\n')
}

/**
 * Zwei Zeilen werden eine, wenn der Anwender eine Dimension abwählt: Ihr
 * Rechenweg ist die Summe beider. Fehlt er an einer, fehlt er an beiden —
 * ein halber Rechenweg ginge nicht auf.
 */
export function vereint(a: Rechenweg | null | undefined, b: Rechenweg | null | undefined): Rechenweg | null {
  if (!a || !b) return null
  const beide = [...new Set([...ziele(a), ...ziele(b)])]
  return {
    ziel: a.ziel,
    ...(beide.length > 1 ? { ziele: beide } : {}),
    schritte: [...a.schritte, ...b.schritte],
    zielwert: a.zielwert + b.zielwert,
    zeilen: a.zeilen + b.zeilen,
    differenz: a.differenz + b.differenz,
  }
}
